import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { trackMkt } from "@/lib/mkt";
import { Button } from "@/components/ui/button";
import { SiteFooter } from "@/components/SiteFooter";
import {
  Camera, Link2, Eye, Check, ArrowRight,
  ClipboardCheck, Sparkles, Gift,
  Send, ChevronRight, Smartphone, MousePointerClick, MessageSquare, X,
  DollarSign, Clock, Fuel,
} from "lucide-react";

/* ---------- Phone mockup — real app screenshot ---------- */
function PhoneMock({ onClick, testId = "phone-demo" }) {
  const interactive = !!onClick;
  return (
    <div className="relative mx-auto w-[280px] sm:w-[320px]">
      {/* device frame */}
      <button
        type="button"
        onClick={onClick}
        disabled={!interactive}
        data-testid={interactive ? testId : undefined}
        className={`group relative block w-full rounded-[2.6rem] border border-border bg-[hsl(222,44%,10%)] p-2.5 text-left shadow-2xl ring-1 ring-white/5 ${interactive ? "cursor-pointer transition-transform duration-200 hover:-translate-y-1 active:scale-[0.99]" : ""}`}
      >
        <div className="overflow-hidden rounded-[2rem] bg-[hsl(222,47%,8%)]">
          <video
            ref={(el) => { if (el) el.muted = true; }}
            src="/media/how-it-works-demo.mp4"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-label="Send-Proof app walkthrough"
            className="pointer-events-none block w-full"
          />
        </div>
        {interactive && (
          <span className="pointer-events-none absolute inset-0 rounded-[2.6rem] ring-2 ring-emerald-500/0 transition-all duration-200 group-hover:ring-emerald-500/50" />
        )}
      </button>
    </div>
  );
}

const SAMPLE_TOKEN = "CurbPourDemo";

/* ---------- Animated count-up ---------- */
function CountUp({ value, className }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf; const start = performance.now(); const dur = 1400;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span className={className}>{n.toLocaleString()}</span>;
}

/* ---------- Proof-stats card ---------- */
function StatsCard({ stats }) {
  const showVerified = stats.verified >= 10;
  return (
    <div
      data-testid="trust-stats"
      className="group relative mt-6 overflow-hidden rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-[hsl(222,44%,13%)] to-[hsl(222,47%,9%)] p-1 shadow-xl shadow-emerald-500/5 ring-1 ring-white/5"
    >
      <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-emerald-500/20 blur-3xl transition-opacity duration-500 group-hover:opacity-80" />
      <div className="relative flex items-center justify-between px-4 pt-3">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Proof, in numbers</span>
        <span className="flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-400">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
          </span>
          Live
        </span>
      </div>
      <div className={`relative grid ${showVerified ? "grid-cols-2 divide-x divide-white/10" : "grid-cols-1"} gap-0 p-3`}>
        <div className="flex items-center gap-3 px-3 py-2">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/20">
            <Camera className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <CountUp value={stats.photos} className="block font-heading text-2xl font-extrabold leading-none tracking-tight text-foreground sm:text-3xl" />
            <span className="mt-1 block text-xs text-muted-foreground">proof photos & counting</span>
          </div>
        </div>
        {showVerified && (
          <div className="flex items-center gap-3 px-3 py-2">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/20">
              <ClipboardCheck className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <CountUp value={stats.verified} className="block font-heading text-2xl font-extrabold leading-none tracking-tight text-foreground sm:text-3xl" />
              <span className="mt-1 block text-xs text-muted-foreground">punch lists verified</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const reveal = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};
const panel = "rounded-2xl border border-white/5 bg-gradient-to-b from-[hsl(222,42%,15%)] to-[hsl(222,46%,10%)] shadow-xl shadow-black/30 ring-1 ring-white/5 transition-all duration-300 hover:-translate-y-1 hover:ring-emerald-500/40 hover:shadow-emerald-500/10";

export default function Landing() {
  const navigate = useNavigate();
  const go = () => navigate("/auth?mode=signup");
  const { tryFree } = useAuth();
  const [tryBusy, setTryBusy] = useState(false);
  const handleTryFree = async (source = "try_free") => {
    trackMkt("cta", source);
    setTryBusy(true);
    try {
      await tryFree();
      try { sessionStorage.setItem("sp_welcome", "1"); } catch { /* ignore */ }
      navigate("/dashboard");
    } catch { setTryBusy(false); go(); }
  };

  const [stats, setStats] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/stats`)
      .then((r) => r.json())
      .then(setStats)
      .catch(() => {});
  }, []);

  // Scroll-depth tracking — each milestone fires once per visit.
  useEffect(() => {
    const seen = new Set();
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      if (max <= 0) return;
      const pct = Math.round((h.scrollTop / max) * 100);
      [25, 50, 75, 100].forEach((d) => {
        if (pct >= d && !seen.has(d)) { seen.add(d); trackMkt("scroll", d); }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Section-reached tracking — fires once when the demo / pricing scroll into view.
  useEffect(() => {
    const targets = [
      { el: document.querySelector('[data-testid="landing-whatitis"]'), name: "whatitis" },
      { el: document.getElementById("pricing"), name: "pricing" },
    ].filter((t) => t.el);
    if (!targets.length) return;
    const seen = new Set();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        const t = targets.find((x) => x.el === en.target);
        if (t && !seen.has(t.name)) { seen.add(t.name); trackMkt("section", t.name); io.unobserve(en.target); }
      });
    }, { threshold: 0.4 });
    targets.forEach((t) => io.observe(t.el));
    return () => io.disconnect();
  }, []);

  const faqs = [
    { q: "Can't I just text each crew member?", a: "You can — but the photos end up scattered across separate threads and you never really know who's actually done. Send-Proof keeps the whole job on one shared list: every trade, every item, a photo on each, and a live status that rolls up to one percentage you watch from your phone — instead of chasing six text chains." },
    { q: "Is there a free version for my crew?", a: "Yes. Anyone you send a list to can create a free account and download the app — it keeps every list you've sent them in one clean place instead of buried in a text chain. The free account opens and fills lists; creating and sending your own reports is the paid side ($9.99/mo)." },
    { q: "Do my crew members need to download an app?", a: "No — they can just tap your link and start marking items and adding photos, nothing to install. If they want, they can grab the free app and get a tidy home for every list you send them, at no cost." },
    { q: "Is my project data secure?", a: "Your photos and reports are private to you. Share links are unguessable and only show the one report you send — nobody can browse your other jobs, and you can stop sharing anytime." },
    { q: "What happens after my trial?", a: "Your 15-day trial is free with no credit card. After that it's $9.99/month for everything — cancel anytime, no lock-in." },
    { q: "Can I cancel anytime?", a: "Yes. One flat plan, month to month. Cancel in a couple of taps — no contract, no lock-in." },
  ];

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-card text-foreground">
      {/* ambient emerald glow */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[1200px]">
        <div className="absolute left-1/2 top-[-200px] h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[130px]" />
        <div className="absolute right-[-160px] top-[560px] h-[440px] w-[440px] rounded-full bg-emerald-500/[0.06] blur-[130px]" />
      </div>
      <div className="relative z-10">
      {/* Nav — sticky: logo left, Sign in + Try free right (mobile-first, CTA always reachable). Sign in also remains in the footer. */}
      <header className="sticky top-0 z-30 border-b border-white/10 bg-[hsl(222,46%,10%)]/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3 sm:px-8 sm:py-3.5">
          <div className="flex items-center" data-testid="landing-logo">
            <img src="/logo-mark.webp" alt="Send-Proof" className="h-11 w-11 rounded-xl sm:h-12 sm:w-12" />
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <button onClick={() => navigate("/auth?mode=signin")} data-testid="header-signin" className="text-sm font-semibold text-emerald-400 hover:text-emerald-300">Sign in</button>
            <Button size="sm" disabled={tryBusy} onClick={() => handleTryFree("header_try_free")} data-testid="header-try-free" className="h-9 rounded-full bg-emerald-500 px-4 text-sm font-bold text-black shadow-md shadow-emerald-500/20 hover:bg-emerald-400">
              {tryBusy ? "…" : "Try free"}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <motion.section variants={reveal} initial="hidden" animate="show" className="mx-auto max-w-6xl px-5 pt-8 sm:px-8 sm:pt-16">
        <div className="grid items-center gap-14 md:grid-cols-2">
          <div className="min-w-0 animate-in-up">
            <h1 className="font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Stop chasing punch lists. Start <span className="bg-gradient-to-r from-emerald-400 to-emerald-500 bg-clip-text text-transparent">Closing them.</span>
            </h1>
            <p className="mt-4 text-lg font-bold text-emerald-400 sm:text-xl" data-testid="landing-tagline">
              Punch lists, finally done right.
            </p>
            <p className="mt-3 max-w-md text-base text-muted-foreground sm:text-lg" data-testid="landing-definition">
              Text your crew a photo punch list — proof comes back on every item. Approve it from your phone and skip the trip back.
            </p>
            <div className="mt-6 text-base font-extrabold uppercase tracking-[0.14em] text-emerald-400 sm:text-lg" data-testid="hero-how-label">How it works</div>
            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold" data-testid="hero-how">
              <span className="text-foreground">Build a list</span>
              <ArrowRight className="h-3.5 w-3.5 text-emerald-500" />
              <span className="text-foreground">text the link</span>
              <ArrowRight className="h-3.5 w-3.5 text-emerald-500" />
              <span className="text-foreground">they snap proof</span>
              <ArrowRight className="h-3.5 w-3.5 text-emerald-500" />
              <span className="text-foreground">you verify</span>
            </div>

            {/* Product shot — mobile, high up (above the proof points) so the first screen isn't empty */}
            <div className="relative mb-6 mt-10 md:hidden">
              <div className="pointer-events-none absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-500/25 blur-[90px]" />
              <div className="pointer-events-none absolute left-1/2 top-[62%] h-40 w-64 -translate-x-1/2 rounded-full bg-emerald-400/10 blur-[60px]" />
              <div className="relative animate-float"><PhoneMock /></div>
            </div>

            <p className="mb-2 text-sm font-extrabold text-emerald-400 sm:text-base" data-testid="landing-noapp">
              No app for your subs. No accounts. No chasing.
            </p>

            {/* Scannable proof points — headline + line, weaving in the advisor's message-bank taglines */}
            <ul className="mt-6 space-y-4" data-testid="hero-proofpoints">
              {[
                { icon: Eye, h: "Know the second they open it", d: "See every open as it happens — stop wondering if your crew even saw the list." },
                { icon: Camera, h: "\u201cDone, Boss\u201d isn't proof.", d: "A photo lands on every item in real time — not just an \u201call done, boss\u201d text." },
                { icon: Check, h: "Your punch list shouldn't live in six text threads.", d: "One shared list holds every item, photo and status — so you hit 100% without driving to the site." },
              ].map(({ icon: Icon, h, d }) => (
                <li key={h} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-400"><Icon className="h-4 w-4" /></span>
                  <div>
                    <p className="text-base font-bold text-foreground">{h}</p>
                    <p className="mt-0.5 text-sm text-muted-foreground">{d}</p>
                  </div>
                </li>
              ))}
            </ul>

            {/* Free offer card — "Glow Ticket" (Option 1): emerald glow + FREE badge, tied to the no-signup CTA */}
            <div className="relative mt-6 max-w-md overflow-hidden rounded-2xl border-2 border-emerald-500/60 bg-gradient-to-b from-[hsl(158,60%,12%)] to-[hsl(222,47%,9%)] p-5 shadow-[0_0_40px_-8px_rgba(16,185,129,0.45)]" data-testid="free-offer-card">
              <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-emerald-500/25 blur-3xl" />
              <div className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-black">
                <Gift className="h-3.5 w-3.5" /> Free
              </div>
              <p className="relative text-xs font-bold uppercase tracking-[0.18em] text-emerald-400">Start now</p>
              <p className="relative mt-1 text-xl font-extrabold leading-tight text-white">The full app — free.<br /><span className="text-emerald-300">No card, no email.</span></p>
              <div className="relative mt-3 flex flex-wrap gap-2">
                {["1 real report", "Up to 40 photos", "Every feature"].map((c) => (
                  <span key={c} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-200 ring-1 ring-emerald-400/30">
                    <Check className="h-3.5 w-3.5" /> {c}
                  </span>
                ))}
              </div>
              <Button size="lg" disabled={tryBusy} className="relative mt-4 h-12 w-full bg-emerald-500 px-8 text-base font-bold text-black shadow-lg shadow-emerald-500/30 hover:bg-emerald-400" data-testid="hero-try-free" onClick={() => handleTryFree("hero_try_free")}>
                {tryBusy ? "Starting…" : <>Try it on a real job — no signup <ArrowRight className="ml-1 h-4 w-4" strokeWidth={3} /></>}
              </Button>
              <p className="relative mt-2.5 text-center text-[11px] text-emerald-200/70">Need unlimited jobs &amp; photos? <button onClick={() => { trackMkt("cta", "hero_unlimited"); go(); }} data-testid="hero-cta" className="font-semibold text-emerald-300 underline underline-offset-2">Go unlimited — $9.99/mo</button></p>
            </div>
          </div>

          {/* Desktop product shot */}
          <div className="relative hidden min-w-0 pb-6 md:block md:pb-0">
            <div className="pointer-events-none absolute left-1/2 top-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-500/25 blur-[110px]" />
            <div className="pointer-events-none absolute left-1/2 top-[58%] h-52 w-72 -translate-x-1/2 rounded-full bg-emerald-400/10 blur-[70px]" />
            <div className="relative animate-float"><PhoneMock /></div>
          </div>
        </div>
      </motion.section>

      {/* What it is — the product line, moved below the pain-led hero. Keeps the phrase + the "punch list" keyword on-page for SEO. */}
      <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mx-auto max-w-3xl px-5 pt-20 text-center sm:px-8" data-testid="landing-whatitis">
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-400">What it is</div>
        <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
          Punch lists that actually get <span className="bg-gradient-to-r from-emerald-400 to-emerald-500 bg-clip-text text-transparent">worked.</span>
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground sm:text-lg">
          Build a list, text the link, and your crew snaps a proof photo on every item — no app for them. You watch each one roll in live and hit 100% without a single drive-by.
        </p>
        <div className="mx-auto mt-10 w-full max-w-[300px]">
          <div className="rounded-[2.6rem] border border-border bg-[hsl(222,44%,10%)] p-2.5 shadow-2xl ring-1 ring-white/5">
            <div className="overflow-hidden rounded-[2rem] bg-[hsl(222,47%,8%)]">
              <video
                ref={(el) => { if (el) el.muted = true; }}
                autoPlay
                muted
                loop
                playsInline
                controls
                preload="none"
                aria-label="Send-Proof walkthrough"
                data-testid="whatitis-video"
                className="block w-full"
              >
                <source src="/media/what-it-is.mp4" type="video/mp4" />
                <source src="https://customer-assets-jai6qajn.emergentagent.net/job_work-verified-7/artifacts/7db5bfb07931ff2a_Screen_Recording_20261010_111749_Chrome.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Sort & Send by Trade — flagship feature, in its own full-bleed band for clear separation from the hero */}
      <div className="relative mt-20 border-t border-white/[0.07] bg-[hsl(222,45%,11%)]" data-testid="sort-send-band">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-emerald-500/[0.05] to-transparent" />
        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="relative mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24">
        <div className="rounded-3xl border border-emerald-500/30 bg-emerald-500/[0.06] p-6 shadow-2xl shadow-emerald-500/5 ring-1 ring-emerald-500/10 sm:p-10">
          <div className="grid items-center gap-10 md:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-white shadow-lg shadow-emerald-500/30" data-testid="sort-send-usp">
                <Sparkles className="h-3.5 w-3.5" /> Only in Send-Proof
              </span>
              <h2 className="mt-4 font-heading text-2xl font-bold tracking-tight sm:text-3xl">One messy punch list in. A clean list for <span className="text-emerald-400">every trade</span> out.</h2>
              <p className="mt-3 max-w-md text-base text-muted-foreground">
                No other punch-list app does this. One tap splits a mixed punch list into a clean list per trade.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-foreground">
                {[
                  "Sorts every item into the right trade automatically",
                  "Send each trade its own live link — or a PDF",
                  "Set one deadline that rides along on every send",
                  "Their check-offs roll straight back into your report",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {f}</li>
                ))}
              </ul>
            </div>
            <div className="mx-auto w-full max-w-[300px]">
              <div className="rounded-[2.6rem] border border-border bg-[hsl(222,44%,10%)] p-2.5 shadow-2xl ring-1 ring-white/5">
                <video
                  ref={(el) => { if (el) el.muted = true; }}
                  src="/media/sort-send-demo.mp4"
                  poster="/shots/dispatch_mobile.webp"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="none"
                  data-testid="sort-send-video"
                  aria-label="Send-Proof sorting one punch list into a clean list for every trade"
                  className="block w-full rounded-[2rem]"
                />
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      </div>

      {/* Why not just text everyone? — MOCKUP: full-bleed band treatment for section separation */}
      <div className="relative mt-20 border-t border-white/[0.07] bg-[hsl(222,45%,11%)]" data-testid="vs-texting-band">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-emerald-500/[0.05] to-transparent" />
        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="relative mx-auto max-w-5xl px-5 py-20 sm:px-8 sm:py-24" data-testid="vs-texting">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-semibold text-emerald-400"><MessageSquare className="h-3.5 w-3.5" /> Isn't this just texting?</span>
          <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">“All done, boss” is not a <span className="bg-gradient-to-r from-emerald-400 to-emerald-500 bg-clip-text text-transparent">closeout system.</span></h2>
          <h3 className="mt-3 font-heading text-xl font-bold tracking-tight text-foreground sm:text-2xl">Why not just text each guy?</h3>
          <p className="mt-3 text-base text-muted-foreground">
            You can — and then the photos are buried across six different threads and you still don't know who's actually done. Send-Proof puts every trade on <span className="font-semibold text-foreground">one shared list</span>, so you see every item, every photo, and who's left — all in one place, live.
          </p>
        </div>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-white/5 bg-[hsl(222,46%,11%)]/60 p-6 ring-1 ring-white/5" data-testid="vs-texting-col">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/5 text-muted-foreground"><MessageSquare className="h-5 w-5" /></span>
              <span className="font-heading text-base font-bold text-foreground">Texting each guy</span>
            </div>
            <ul className="mt-4 space-y-3">
              {[
                "Photos scattered across separate threads",
                "No idea who opened it — or who's done",
                "You chase every sub for a status",
                "Nothing's timestamped or verifiable",
                "\"Done\" still means driving out to check",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-red-400/80" strokeWidth={2.5} /> <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.06] p-6 ring-1 ring-emerald-500/10" data-testid="vs-sendproof-col">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400"><ClipboardCheck className="h-5 w-5" /></span>
              <span className="font-heading text-base font-bold text-foreground">One shared list</span>
            </div>
            <ul className="mt-4 space-y-3">
              {[
                "Every trade and item on one live board",
                "See who's opened it and what's left — at a glance",
                "A photo on every item, timestamped",
                "One job % that rolls up the whole crew",
                "\"Done\" means you already saw the proof",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-foreground">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" strokeWidth={2.5} /> <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        </motion.section>
      </div>

      {/* Who it's for — REMOVED (lean 5-card page) */}

      {/* Sample proof report */}
      <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mx-auto max-w-6xl px-5 pt-20 sm:px-8" data-testid="subs-will-use-it">
        <div className="mb-6 flex justify-center">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-emerald-300" data-testid="hero-eyebrow">
            <Check className="h-3.5 w-3.5" /> No app for your crew
          </span>
        </div>
        <div className="overflow-hidden rounded-3xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.12] to-emerald-500/[0.03] p-8 ring-1 ring-emerald-500/20 sm:p-12">
          <div className="grid items-center gap-10 md:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-emerald-400"><Smartphone className="h-3.5 w-3.5" /> What your sub sees</span>
              <h2 className="mt-4 font-heading text-2xl font-bold tracking-tight sm:text-3xl">No app. No login. Free for your crew.</h2>
              <p className="mt-3 max-w-md text-base text-muted-foreground">They tap your link, snap a photo on each item, hit send. If they can reply to a text, they can use this.</p>
              <ol className="mt-6 flex flex-wrap items-center gap-2 text-sm font-semibold" data-testid="subs-flow">
                {["No account", "No download", "One tap", "Photo each item", "Done"].map((s, i, arr) => (
                  <li key={s} className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1.5 text-emerald-300 ring-1 ring-emerald-500/20"><Check className="h-3.5 w-3.5" /> {s}</span>
                    {i < arr.length - 1 && <ChevronRight className="h-4 w-4 text-emerald-500/50" />}
                  </li>
                ))}
              </ol>
              <Button className="mt-6 h-11 gap-1 bg-emerald-500 hover:bg-emerald-600" data-testid="subs-see-it-btn" onClick={() => { trackMkt("cta", "see_sample"); navigate(`/r/${SAMPLE_TOKEN}`); }}>
                Open the exact screen they get <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="relative mx-auto w-[260px] sm:w-[300px]" data-testid="subs-share-mock">
              <span className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-emerald-500 px-4 py-1.5 text-xs font-bold text-white shadow-lg shadow-emerald-500/30">Exactly what your sub sees</span>
              <div className="rounded-[2.4rem] border border-border bg-[hsl(222,44%,10%)] p-2.5 shadow-2xl ring-1 ring-white/5">
                <div className="overflow-hidden rounded-[1.9rem] bg-[hsl(222,47%,8%)]">
                  <video
                    ref={(el) => { if (el) el.muted = true; }}
                    src="/media/sub-view-demo.mp4"
                    poster="/shots/share_mobile.webp"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="none"
                    data-testid="subs-view-video"
                    aria-label="Screen recording of the share link a subcontractor opens — no account, tap and add a photo"
                    className="block w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Pricing — the close, in its own full-bleed band */}
      <div className="relative mt-20 border-t border-white/[0.07] bg-[hsl(222,45%,11%)]">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-emerald-500/[0.05] to-transparent" />
        <motion.section id="pricing" variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="relative mx-auto max-w-md scroll-mt-24 px-5 py-20 sm:px-8 sm:py-24">
        <div className="relative rounded-2xl border-2 border-emerald-500 bg-gradient-to-b from-[hsl(222,44%,14%)] to-[hsl(222,47%,9%)] p-7 shadow-2xl shadow-emerald-500/20" data-testid="pricing-card">
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-500">Everything, one plan</div>
          <p className="mt-2 text-sm font-semibold text-emerald-400">Less than a single site visit.</p>
          <div className="mt-1 flex items-end gap-1">
            <span className="font-heading text-5xl font-extrabold">$9.99</span>
            <span className="pb-2 text-muted-foreground">/month</span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">One flat price for you — $9.99/month. Everyone you send lists to opens them free, no account needed.</p>
          <p className="mt-2 text-sm text-muted-foreground">15-day free trial. <span className="font-semibold text-foreground">No credit card required.</span> Cancel anytime.</p>
          <ul className="mt-5 space-y-2 text-sm text-foreground">
            {["Unlimited jobs & reports", "Unlimited photos", "Real-time open tracking", "Shareable proof links + QR"].map((f) => (
              <li key={f} className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-500" /> {f}
              </li>
            ))}
          </ul>
          <Button disabled={tryBusy} className="mt-6 h-12 w-full bg-emerald-500 text-base hover:bg-emerald-600" data-testid="pricing-cta" onClick={() => handleTryFree("pricing")}>
            {tryBusy ? "Starting…" : <>Try it on a real job today <ArrowRight className="ml-1 h-4 w-4" /></>}
          </Button>
          <p className="mt-3 text-center text-xs text-muted-foreground">Starts free — no card, no email. Add billing only when you're ready to go unlimited.</p>
        </div>
        </motion.section>
      </div>

      {/* Payoff + live proof — moved out of the hero into its own card above the FAQ */}
      <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mx-auto max-w-2xl px-5 pt-24 sm:px-8" data-testid="payoff-card">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-xl sm:p-8">
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">What you get back</h2>
          <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm" data-testid="hero-outcomes">
            <span className="inline-flex items-center gap-1.5 text-muted-foreground"><Clock className="h-4 w-4 text-emerald-500" /> Hours back a week</span>
            <span className="inline-flex items-center gap-1.5 text-muted-foreground"><Fuel className="h-4 w-4 text-emerald-500" /> A tank of gas saved</span>
            <span className="inline-flex items-center gap-1.5 text-muted-foreground"><DollarSign className="h-4 w-4 text-emerald-500" /> More jobs a day</span>
          </div>

          {/* Per-seat contrast — speaks to tool-comparison traffic (highest intent) */}
          <div className="mt-6 flex items-start gap-3 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.07] px-4 py-3" data-testid="per-seat-contrast">
            <DollarSign className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
            <p className="text-sm text-foreground">
              Other apps charge <span className="font-semibold">per seat</span>. Your subs use it <span className="font-semibold text-emerald-400">free</span> — you pay <span className="font-semibold">$9.99</span>.
            </p>
          </div>
          {stats && stats.photos > 0 && <StatsCard stats={stats} />}
        </div>
      </motion.section>

      {/* FAQ — compact accordion, in its own full-bleed band */}
      <div className="relative mt-20 border-t border-white/[0.07] bg-[hsl(222,45%,11%)]">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-emerald-500/[0.05] to-transparent" />
        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="relative mx-auto max-w-2xl px-5 py-20 sm:px-8 sm:py-24" data-testid="faq">
        <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Questions before you start</h2>
        <div className="mt-8 space-y-3">
          {faqs.map((f) => (
            <details key={f.q} className={`${panel} group p-5`} data-testid="faq-item">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-heading text-base font-bold">
                {f.q}
                <ChevronRight className="h-5 w-5 shrink-0 text-emerald-500 transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
        </motion.section>
      </div>

      {/* Footer */}
      <SiteFooter />

      </div>
    </div>
  );
}
